<template>
  <Badge
    v-if="display"
    :variant="display.variant"
    class="flex items-center gap-1 px-1.5 py-0 text-[10px] font-medium normal-case"
  >
    <CalendarIcon class="h-3 w-3" />
    {{ display.label }}
  </Badge>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { OneOffTaskEntity } from '@/api/entity_types/OneOffTask';
import type { UserTaskEntity } from '@/api/entity_types/UserTask';
import { Badge, type BadgeVariants } from '@/components/ui/badge';
import DateUtility from '@/utilities/dates';
import { CalendarIcon } from 'lucide-vue-next';

type Variant = NonNullable<BadgeVariants['variant']>;
type Display = { variant: Variant; label: string };

const props = defineProps<{
  task: UserTaskEntity;
  today?: Date;
}>();

const parsed = computed<Date | null>(() => {
  if (props.task.taskable_type !== 'OneOffTask') return null;
  return DateUtility.parseLocalDate((props.task.taskable as OneOffTaskEntity)?.due_date);
});

const dateString = computed(() => parsed.value?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) ?? '');

const display = computed<Display | null>(() => {
  const due = parsed.value;
  if (!due) return null;
  if (props.task.status === 'completed') return { variant: 'gray', label: dateString.value };

  const todayStart = startOfDay(props.today ?? new Date());
  const dueStart = startOfDay(due);
  if (dueStart < todayStart) return { variant: 'red', label: 'Overdue' };

  const diffDays = Math.floor((dueStart.getTime() - todayStart.getTime()) / 86_400_000);
  if (diffDays === 0) return { variant: 'yellow', label: 'Today' };
  if (diffDays === 1) return { variant: 'gray', label: 'Tomorrow' };
  return { variant: 'gray', label: dateString.value };
});

const startOfDay = (d: Date) => {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
};
</script>
